import { useEffect, useRef, useState } from 'react';

import { Button } from './PageHeading';
import { decodeFrame, scanScale } from './qrScan';

/**
 * Reads a garment tag through the device camera.
 *
 * <p>
 * The handheld scanner beside it needs none of this — it is a keyboard, and typing into the search
 * box is the whole interaction. This is for the phone in a storeman's hand at the rail, where there
 * is no keyboard and no scanner, and the tag is right there.
 * </p>
 *
 * <p>
 * <strong>The stream is the thing to get right.</strong> A camera left running is a light left on
 * in a shop: the browser keeps the indicator lit, the battery drains, and nothing on screen says
 * why. Every exit from this component stops every track — the close button, the Escape key, an
 * unmount, and the successful read itself, which closes on the way out.
 * </p>
 *
 * <p>
 * It refuses gracefully rather than cleverly. No camera, a refused permission, or a page served
 * over plain HTTP each get a plain sentence and the typed field still works — this is the second
 * way in, never the only one.
 * </p>
 */
export function CameraScanner({
  onRead,
  onClose,
}: {
  /** The decoded payload, already reduced to a serial. Called once; the panel then closes. */
  onRead: (serial: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);

  // A ref as well as state: the decode loop is a closure that outlives a render, and reading the
  // flag off state there would let two frames both fire `onRead` before the first re-render lands.
  const doneRef = useRef(false);

  const [error, setError] = useState<string | null>(null);

  /**
   * Whether the stream has actually arrived.
   *
   * <strong>This is the whole state of the panel while the browser's permission bubble is up</strong>,
   * and getting it wrong was reported the first time this was opened against a real camera: the
   * video is black, `getUserMedia` has not resolved, and the panel was saying "it reads on its own —
   * there is nothing to press" at the exact moment the only thing that would help is pressing Allow.
   * A person who does not notice the bubble waits at a black square being told to keep waiting.
   */
  const [live, setLive] = useState(false);

  useEffect(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    let cancelled = false;

    function stop() {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }

      for (const track of streamRef.current?.getTracks() ?? []) {
        track.stop();
      }

      streamRef.current = null;
    }

    function read() {
      const video = videoRef.current;

      if (cancelled || doneRef.current || !video || !context) {
        return;
      }

      // `readyState` rather than a timer: a frame drawn before the first one has arrived is a blank
      // canvas, and decoding blanks for the first second wastes the moment somebody is aiming.
      if (video.readyState >= video.HAVE_CURRENT_DATA) {
        const scale = scanScale(video.videoWidth, video.videoHeight);

        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        const serial = decodeFrame(frame.data, frame.width, frame.height);

        if (serial !== null) {
          doneRef.current = true;
          stop();
          onRead(serial);
          return;
        }
      }

      frameRef.current = requestAnimationFrame(read);
    }

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          window.isSecureContext
            ? 'This browser will not open a camera.'
            : 'A camera needs a secure connection. Open the app over https.',
        );
        return;
      }

      try {
        // `ideal`, not `exact`: a phone should use the rear camera, and a desk with one webcam
        // should still work rather than failing to find a camera facing away from the person.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
        });

        if (cancelled) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }

        setLive(true);

        frameRef.current = requestAnimationFrame(read);
      } catch (cause) {
        const name = cause instanceof DOMException ? cause.name : '';

        setError(
          name === 'NotAllowedError'
            ? 'The camera was not allowed. Type the serial instead, or allow it and try again.'
            : name === 'NotFoundError'
              ? 'No camera on this device. Type the serial instead.'
              : 'The camera could not be opened. Type the serial instead.',
        );
      }
    }

    void start();

    return () => {
      cancelled = true;
      stop();
    };
  }, [onRead]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // `data-shortcut-layer`: while this is open the vocabulary is this panel's, so Alt+P does not
    // print the page behind it. Mounted from page content, which is rendered after the sidebar, so
    // it covers the sidebar at the z-9999 the two share.
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 print:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Scan a garment tag"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-lg border border-[var(--overlay-border)] bg-[var(--overlay-surface)] p-4 shadow-lg">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            Scan a garment tag
          </h2>
          <Button icon="icon icon-x" aria-label="Close" onClick={onClose} />
        </div>

        {error === null ? (
          <>
            <div className="relative overflow-hidden rounded-md bg-dark">
              {/* Muted and inline, or iOS refuses to play it without a gesture and shows nothing. */}
              <video
                ref={videoRef}
                className="block h-64 w-full object-cover"
                muted
                playsInline
                aria-label="Camera preview"
              />

              {/* The aiming frame. Decoding uses the whole frame — this only tells a person where
                  to hold the tag, so it must not imply the edges are ignored. Drawn only once there
                  is something to aim at; over a black box it is furniture pretending to be a camera. */}
              {live && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 flex items-center justify-center"
                >
                  <div className="size-40 rounded-md border-2 border-white/70" />
                </div>
              )}

              {!live && (
                <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
                  <p className="text-[length:var(--scale-list-meta)] text-white/80">
                    Asking for the camera…
                  </p>
                </div>
              )}
            </div>

            <p
              className="mt-3 text-[length:var(--scale-list-meta)] text-default"
              aria-live="polite"
            >
              {live
                ? 'Hold the tag inside the square. It reads on its own — there is nothing to press.'
                : 'Your browser is asking whether this page may use the camera. Allow it to carry on, or type the serial instead.'}
            </p>
          </>
        ) : (
          <p className="text-[length:var(--scale-font-size)] text-default">{error}</p>
        )}
      </div>
    </div>
  );
}
